import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute } from "@tanstack/react-router";

import { SituationPage } from "@/components/situation-page";
import { FIRM, faqSchema } from "@/lib/firm";

const FAQS = [
  {
    q: "What is a Form 2 and why does it matter to a buyer?",
    a: "Since 1 August 2025, most Queensland sellers must give buyers a seller disclosure statement (Form 2) with certain certificates before the buyer signs the contract. It tells you key facts about the property, such as the title, encumbrances, zoning, rates and any body corporate details.",
  },
  {
    q: "What should I check in the Form 2 before I sign?",
    a: "Easements and other things registered on the title, zoning and any notices from council or the state, pool safety status, rates and water charges, and for units the body corporate information. If anything looks unusual or is missing, ask before you sign.",
  },
  {
    q: "What if I didn't get a Form 2 before signing?",
    a: "If the seller was required to give one and didn't, you may be able to end the contract before settlement. The timing and the way you give notice matter, so get advice straight away.",
  },
  {
    q: "What if the Form 2 is wrong or incomplete?",
    a: "If the statement is inaccurate or incomplete in a way that matters, and you wouldn't have signed had you known, you may be able to end the contract before settlement. Small errors may not be enough. We can look at the statement and tell you where you stand.",
  },
  {
    q: "Does the Form 2 replace my own searches?",
    a: "No. It covers the key disclosures, but your solicitor still checks the title and orders the searches relevant to your purchase, and you still need your own building and pest inspection.",
  },
  {
    q: "Can I still pull out during cooling-off?",
    a: "Yes. The Form 2 doesn't change your usual cooling-off rights. Cooling-off is 5 business days for most residential purchases, with a small termination penalty.",
  },
];

export const Route = createFileRoute("/form-2-buyer-rights-qld")({
  head: () => ({
    meta: [
      { title: fitTitle("Form 2 Seller Disclosure: Buyer Rights QLD | Coastside Law") },
      {
        name: "description",
        content: fitDescription(
          "What Queensland buyers should check in a Form 2 seller disclosure statement, and what you can do if it's missing, wrong or incomplete. Free contract review.",
        ),
      },
      { property: "og:title", content: "Form 2 seller disclosure: your rights as a buyer" },
      {
        property: "og:description",
        content: fitDescription("What to check in the Form 2 and what to do if it's missing or wrong."),
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://coastsidelaw.com.au/form-2-buyer-rights-qld" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://coastsidelaw.com.au/form-2-buyer-rights-qld" }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) }],
  }),
  component: () => (
    <SituationPage
      eyebrow="Buying property"
      title="Form 2 seller disclosure: your rights as a buyer"
      lead="Queensland sellers now have to give you key facts about the property before you sign. Here's what to check, and what you can do if the Form 2 is missing or wrong."
      boxTitle="The short version"
      boxItems={[
        "Most sellers must give you a Form 2 before you sign",
        "Check the title, easements, zoning, notices, pool and body corporate details",
        "Missing or materially wrong? You may be able to end the contract before settlement",
        "It doesn't replace your own searches or building and pest",
        "Your cooling-off rights still apply",
      ]}
      faqs={FAQS}
      links={[
        { to: "/conveyancing/caloundra", label: "Caloundra conveyancing and free pre-signing contract review" },
        { to: "/conveyancing-sunshine-coast", label: "Sunshine Coast conveyancing: fees and solicitor advice" },
        { to: "/conveyancing/redcliffe", label: "Buying on the Redcliffe Peninsula? Conveyancing advice" },
        { to: "/seller-disclosure", label: "Selling? Our Form 2 service" },
        { to: "/cooling-off-period-qld", label: "Cooling-off period in QLD" },
        { to: "/buyers-solicitor-qld", label: "Buyer's solicitor" },
        { to: "/special-conditions-contract-qld", label: "Special conditions explained" },
      ]}
      formHeading="Send us the contract and Form 2 for a free review"
      ctaTitle="Something wrong with the Form 2?"
      ctaBody={`Call ${FIRM.phone}. Timing matters, so talk to us before settlement.`}
    />
  ),
});
